import { createRoot } from 'react-dom/client';
import { toPng, toJpeg } from 'html-to-image';
import { CADSheetBody, DEFAULT_VISIBILITY } from '@/components/cad/CADSheetBody';

/**
 * Render `<CADSheetBody>` offscreen and return a data URL of the consolidated sheet.
 *
 * Two presets:
 *   - `mode: 'thumbnail'` (default) — small JPEG, used as `task.cad_image`. The task
 *     summary shows it at ~180px so we don't need export-grade quality, and a small
 *     payload keeps Save fast. Typical output: 50–200 KB.
 *   - `mode: 'export'` — full-quality PNG at 3x. Match this with the bulk-download
 *     path in AllSheetsPanel.handleBulkDownload if you need print-ready output.
 *
 * Returns `null` if the sheet has no images uploaded — there's nothing useful to
 * render and the caller can skip the upload.
 */
export async function renderSheetToPng(data, opts = {}) {
  const { showCompanyFooter = true, mode = 'thumbnail' } = opts;

  // No views uploaded → bail. The task already has metadata; rendering an empty
  // skeleton just bloats the payload without adding signal.
  const hasAnyImage = (data?.images || []).some(img => !!img?.url);
  if (!hasAnyImage) return null;

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;pointer-events:none;';
  document.body.appendChild(container);
  const root = createRoot(container);

  try {
    await new Promise((resolve) => {
      root.render(
        <CADSheetBody
          data={data}
          visibility={DEFAULT_VISIBILITY}
          showCompanyFooter={showCompanyFooter}
        />
      );
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    // Wait for any embedded images to finish loading before capture. Most are data
    // URLs and resolve synchronously; the per-image timeout caps any stragglers.
    const imgs = Array.from(container.querySelectorAll('img'));
    await Promise.all(imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        setTimeout(done, 2000);
      });
    }));

    const target = container.firstElementChild;
    if (!target) throw new Error('CAD sheet body did not render');

    const pages = Array.from(target.querySelectorAll('.cad-sheet-page-print'));
    if (pages.length === 0) {
      throw new Error('No pages found to render');
    }

    const imageUrls = [];
    for (const page of pages) {
      let dataUrl;
      if (mode === 'export') {
        dataUrl = await toPng(page, { pixelRatio: 3, backgroundColor: '#ffffff' });
      } else {
        // thumbnail: small JPEG. pixelRatio 1 + quality 0.7 keeps the save payload
        // ~10× smaller than a 2x PNG without losing readability at the summary size.
        dataUrl = await toJpeg(page, { pixelRatio: 1, quality: 0.7, backgroundColor: '#ffffff' });
      }
      imageUrls.push(dataUrl);
    }

    return JSON.stringify(imageUrls);
  } finally {
    root.unmount();
    document.body.removeChild(container);
  }
}
